// Through the accessor every test mock of fetchWrapper.js provides; it is
// the same singleton, not a React hook.
import { useFetchWrapper as getFetchWrapper } from './fetchWrapper.js';
import { isSignedIn } from './safeRedirect.js';
import config from '../config.js';
import { SongShakeFX } from './fxEngine.js';
import { captureEvent } from './analytics.js';

/*
 * SongShake FX packages from the registry (ENG-803 backend, ENG-804 frontend).
 *
 *   GET /public/fx/config       no session, cached 60 s →
 *     { rules: FxPackage (songshake-rules), defaults: {vibe, boost},
 *       packages: [the default vibe set and boost] }
 *   GET /public/fx/packages?ids=a,b   1-20 ids → { packages, unknown }
 *   GET /me/fx/loadout          session → { vibe, boost }
 *
 * The engine has no version (ENG-871): a config's `engine` and a package's
 * `core`, which older backends still send, are ignored.
 *
 * A package is JSON in exactly the shape `SongShakeFX.registerPackage()`
 * takes, `{format: 1, type, id, name, version, updated, usesEmoji, emoji,
 * shapes, settings}`, and is registered as is. Never mutate a
 * registered package's settings: every stage that uses it shares the object.
 * For a per-viewer variant, pass `withOverrides(id, {...})` (a fresh settings
 * object) instead of the id.
 *
 * The engine's `settingsOf` throws on an id nobody registered, so every id
 * that reaches it goes through `resolveFxPackage`, which falls back to the
 * slot's default (`config.defaults`) for an id that is unknown, failed to
 * load, or is a package of another type. When even the default is missing
 * it answers null, and the engine draws its own built-in look.
 *
 * The config is read once per page load and shared; a failed read is
 * forgotten so the next stage mount asks again.
 */

export const FX_TYPES = Object.freeze(['vibe', 'boost', 'rules']);
/** Most ids `GET /public/fx/packages` takes per request. */
export const FX_PACKAGE_BATCH = 20;

const isPlainObject = (x) => Boolean(x) && typeof x === 'object' && !Array.isArray(x);
const isId = (x) => typeof x === 'string' && x.trim().length > 0;

/** Reports FX the backend sent that this app can't use (`fx_config_rejected`, ENG-871). */
const reportRejected = (reason, count = 1) => {
  if (count > 0) void captureEvent('fx_config_rejected', { reason, count });
};

/** Registers each package, then reports how many were refused. */
const registerAll = (packages) => {
  const list = Array.isArray(packages) ? packages : [];
  const refused = list.filter((pkg) => !registerFxPackage(pkg)).length;
  if (SongShakeFX) reportRejected('package_rejected', refused);
};

/**
 * The package if it is one the engine can register, else null. It is the
 * same object, not a copy: packages are passed straight in.
 */
export const validFxPackage = (pkg, type = null) => {
  if (!isPlainObject(pkg)) return null;
  if (pkg.format !== 1 || !FX_TYPES.includes(pkg.type) || !isId(pkg.id)) return null;
  if (type && pkg.type !== type) return null;
  if (!isPlainObject(pkg.settings)) return null;
  if (pkg.shapes != null && !isPlainObject(pkg.shapes)) return null;
  return pkg;
};

/** Registers a package with the engine; false when it is not one or the engine refused it. */
export const registerFxPackage = (pkg, type = null) => {
  const valid = validFxPackage(pkg, type);
  if (!valid || !SongShakeFX) return false;
  try {
    SongShakeFX.registerPackage(valid);
    return true;
  } catch (_e) {
    return false;
  }
};

/** The registered package with this id and type, or null. */
export const registeredFxPackage = (id, type) => {
  if (!isId(id) || !SongShakeFX) return null;
  const pkg = SongShakeFX.getPackage(id);
  return pkg && (!type || pkg.type === type) ? pkg : null;
};

/**
 * The id to hand the engine for `type`: `id` when it is registered as that
 * type, else the slot's default when that is, else null (the engine's
 * built-in look). `defaults` is `config.defaults`.
 */
export const resolveFxPackage = (id, type, defaults = null) => {
  if (registeredFxPackage(id, type)) return id;
  const fallback = defaults?.[type];
  if (registeredFxPackage(fallback, type)) return fallback;
  return null;
};

/**
 * A fresh settings object: the package's settings with `overrides` on top,
 * for a per-viewer variant (e.g. reduced effects) the engine takes in place
 * of an id. The registered package is not touched. Null when `id` is not
 * registered as `type`.
 */
export const withOverrides = (id, type, overrides = {}) => {
  const pkg = registeredFxPackage(id, type);
  if (!pkg) return null;
  return { ...pkg.settings, ...(isPlainObject(overrides) ? overrides : {}) };
};

/** `{vibe, boost}` package ids, or null. */
export const normalizeLoadout = (input) => {
  if (!isPlainObject(input) || !isId(input.vibe) || !isId(input.boost)) return null;
  return { vibe: input.vibe, boost: input.boost };
};

/**
 * The `/public/fx/config` body, checked and registered: the rules package and
 * the default packages go into the engine. Throws `{code: 'invalid_config'}`
 * when the body is not a config, so FX can't run.
 */
export const applyFxConfig = (body) => {
  const defaults = isPlainObject(body) ? normalizeLoadout(body.defaults) : null;
  if (!defaults) {
    reportRejected('invalid_config');
    throw { code: 'invalid_config' };
  }
  const rulesId = registerFxPackage(body.rules, 'rules') ? body.rules.id : null;
  if (body.rules != null && !rulesId && SongShakeFX) reportRejected('package_rejected');
  registerAll(body.packages);
  return Object.freeze({ rulesId, defaults: Object.freeze(defaults) });
};

let configPromise = null;

/**
 * The FX config for this page, `{rulesId, defaults}`, read once and
 * shared. Rejects as `applyFxConfig` does, or with the fetchWrapper error.
 */
export const loadFxConfig = () => {
  if (!configPromise) {
    configPromise = getFetchWrapper()
      .get(`${config.apiBaseUrl}/public/fx/config`)
      .then(applyFxConfig)
      .catch((err) => {
        configPromise = null;
        throw err;
      });
  }
  return configPromise;
};

// Ids the registry said it has no package for, so they are not asked again.
const unknownIds = new Set();
// Ids being fetched right now, to the request that is fetching them.
const pendingIds = new Map();

/**
 * Makes sure the packages with these ids are registered, fetching the ones
 * that are not (20 ids a request). Resolves once every request has answered,
 * whatever it answered; a failed request leaves its ids unregistered, and
 * `resolveFxPackage` then falls back to the default.
 */
export const ensureFxPackages = (ids) => {
  if (!SongShakeFX) return Promise.resolve();
  const wanted = [...new Set((Array.isArray(ids) ? ids : [ids]).filter(isId))];
  const waits = [];
  const missing = [];
  wanted.forEach((id) => {
    if (SongShakeFX.getPackage(id) || unknownIds.has(id)) return;
    if (pendingIds.has(id)) waits.push(pendingIds.get(id));
    else missing.push(id);
  });
  for (let i = 0; i < missing.length; i += FX_PACKAGE_BATCH) {
    const batch = missing.slice(i, i + FX_PACKAGE_BATCH);
    const query = batch.map(encodeURIComponent).join(',');
    const request = getFetchWrapper()
      .get(`${config.apiBaseUrl}/public/fx/packages?ids=${query}`)
      .then((body) => {
        registerAll(body?.packages);
        (Array.isArray(body?.unknown) ? body.unknown : []).forEach((id) => { if (isId(id)) unknownIds.add(id); });
      })
      .catch(() => {})
      .finally(() => batch.forEach((id) => pendingIds.delete(id)));
    batch.forEach((id) => pendingIds.set(id, request));
    waits.push(request);
  }
  return Promise.all(waits).then(() => undefined);
};

/** The photosensitive alternative a registered package names (ENG-866), or null. */
export const photosensitiveAlternativeOf = (id, type) => {
  const alt = registeredFxPackage(id, type)?.photosensitiveAlternativeId;
  return isId(alt) ? alt : null;
};

/**
 * `ensureFxPackages`, then the photosensitive alternatives those packages
 * name, so Photosensitivity mode can switch to them without waiting.
 */
export const ensureFxPackagesAndAlternatives = async (ids, type = null) => {
  await ensureFxPackages(ids);
  const list = Array.isArray(ids) ? ids : [ids];
  const alts = list.map((id) => photosensitiveAlternativeOf(id, type)).filter(Boolean);
  if (alts.length) await ensureFxPackages(alts);
};

/**
 * What shows in place of package `id` in Photosensitivity mode (ENG-866):
 * the package's own photosensitive alternative when it names one that is
 * registered as `type`, else the default of `type` (SongShake Classic,
 * songshake-boost), whose reduced look is unchanged (`REDUCED_VIBE`,
 * `REDUCED_BOOST`). Null when not even the default is registered.
 * Use it only while effects are reduced; it never looks at that itself.
 */
export const photosensitiveFxPackage = (id, type, defaults = null) => {
  const alt = photosensitiveAlternativeOf(id, type);
  if (alt && registeredFxPackage(alt, type)) return alt;
  return resolveFxPackage(defaults?.[type] ?? null, type, defaults);
};

/**
 * This viewer's loadout, `{vibe, boost}` package ids: `GET /me/fx/loadout`
 * when signed in, else (and when that fails) the config's defaults.
 */
export const loadFxLoadout = async (defaults) => {
  if (!isSignedIn()) return defaults;
  try {
    return normalizeLoadout(await getFetchWrapper().get(`${config.apiBaseUrl}/me/fx/loadout`)) ?? defaults;
  } catch (_e) {
    return defaults;
  }
};

/** Tests only: forget the config and the unknown ids, as a fresh page load. */
export const clearFxPackageCache = () => {
  configPromise = null;
  unknownIds.clear();
  pendingIds.clear();
};
